import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

type HeadingTag = {
	label: string;
	count?: number;
	active?: boolean;
};

type HeadingProps = ComponentProps<'div'> & {
	/** NOTE: Match the heading level to the section's document hierarchy. */
	as?: 'h1' | 'h2' | 'h3';
	/** NOTE: Allow compact card and full-width section compositions to share the primitive. */
	variant?: VariantProps<typeof headingVariants>['variant'];
	/** Solid half of the duo-tone headline */
	titlePrefix: string;
	/** Render the localized conjunction between the two headline halves. */
	ampersand?: boolean;
	/** Gradient half of the duo-tone headline; rendered after a muted "&" */
	titleHighlight: string;
	/** Muted context line below the headline */
	description?: string;
	tags?: HeadingTag[];
};

const headingVariants = cva('w-full', {
	variants: {
		variant: {
			start: 'text-start',
			compact: 'max-w-xl text-start',
			centered: 'mx-auto text-center',
		},
	},
	defaultVariants: {
		variant: 'centered',
	},
});

export function Heading({
	className,
	as: HeadingElement = 'h2',
	variant = 'centered',
	titlePrefix,
	ampersand,
	titleHighlight,
	description,
	tags,
	...props
}: HeadingProps) {
	const isCentered = variant === 'centered';

	return (
		<div className={cn(headingVariants({ variant }), className)} {...props}>
			<HeadingElement
				className={cn(
					'max-w-[20ch] text-balance font-xblk text-2xl text-foreground-light leading-[0.98] tracking-[-0.025em] md:text-4xl',
					isCentered && 'mx-auto text-center',
				)}
			>
				<span className="text-foreground-light">{titlePrefix}</span>{' '}
				{ampersand && (
					<>
						<span
							aria-hidden
							className="font-souvenir text-muted-foreground italic"
						>
							{m.heading_conjunction()}
						</span>{' '}
					</>
				)}
				<span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent rtl:bg-linear-to-l">
					{titleHighlight}
				</span>
			</HeadingElement>
			{description && (
				<p
					className={cn(
						'mt-3 max-w-2xl text-pretty text-muted-foreground text-sm capitalize leading-relaxed md:text-base lg:mt-4',
						isCentered && 'mx-auto text-center',
					)}
				>
					{description}
				</p>
			)}
			{tags?.length ? (
				<ul
					className={cn(
						'mt-6 flex flex-wrap gap-2',
						isCentered && 'justify-center',
					)}
				>
					{tags.map((tag) => {
						const isActive = tag.active;
						return (
							<li key={tag.label}>
								<span
									className={cn(
										'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-2xs tracking-widest transition-colors',
										isActive
											? 'border-border bg-card text-primary'
											: 'border-border/60 text-muted-foreground hover:border-border/80 hover:text-foreground',
									)}
								>
									{tag.label}
									{typeof tag.count === 'number' && (
										<span aria-hidden className="text-muted-foreground text-xs">
											{tag.count}
										</span>
									)}
								</span>
							</li>
						);
					})}
				</ul>
			) : null}
		</div>
	);
}
